import { useEffect, useState, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import KnowledgeFormDialog from "@/components/dialogs/KnowledgeFormDialog";
import api, { fileUrl, downloadUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, Link as LinkIcon, Trash2, Pencil, BookOpen, Eye, Download } from "lucide-react";

export default function KnowledgeHub() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [resources, setResources] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);

  const load = () => api.get("/knowledge").then((r) => setResources(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    resources.forEach((r) => {
      if (!map[r.category]) map[r.category] = [];
      map[r.category].push(r);
    });
    return map;
  }, [resources]);

  const deleteResource = async (id) => {
    try {
      await api.delete(`/knowledge/${id}`);
      toast.success("Resource removed");
      load();
    } catch (e) {
      toast.error("Could not remove resource");
    }
  };

  const openAdd = () => {
    setEditingResource(null);
    setFormOpen(true);
  };

  const openEdit = (resource) => {
    setEditingResource(resource);
    setFormOpen(true);
  };

  return (
    <DashboardLayout title="Knowledge Hub">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#4B5563]">Onboarding material, consulting playbooks, frameworks and corporate templates.</p>
        {isAdmin && (
          <Button data-testid="add-knowledge-button" onClick={openAdd} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Resource
          </Button>
        )}
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg py-16 text-center text-[#4B5563]" data-testid="knowledge-empty-state">
          <BookOpen className="h-8 w-8 mx-auto mb-2 text-[#76C121]" />
          No resources added yet.
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`knowledge-category-${category}`}>
            <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4B5563]">{category}</h3>
            </div>
            <div>
              {items.map((item) => (
                <div key={item.id} data-testid={`knowledge-item-${item.id}`} className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-md bg-[#F7FEE7] flex items-center justify-center shrink-0">
                      {item.link ? <LinkIcon className="h-4 w-4 text-[#00823B]" /> : <FileText className="h-4 w-4 text-[#00823B]" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1C1C1E] truncate">{item.title}</p>
                      {item.description && <p className="text-xs text-[#4B5563] truncate">{item.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noreferrer" data-testid={`knowledge-open-link-${item.id}`} className="text-xs font-medium text-[#00823B] hover:underline">
                        Open
                      </a>
                    )}
                    {item.file_path && (
                      <>
                        <a href={fileUrl(item.file_path)} target="_blank" rel="noreferrer" data-testid={`knowledge-view-${item.id}`} title="View" className="text-[#4B5563] hover:text-[#1C1C1E]">
                          <Eye className="h-4 w-4" />
                        </a>
                        <a href={downloadUrl(item.file_path, item.title)} data-testid={`knowledge-download-${item.id}`} title="Download" className="text-[#00823B]">
                          <Download className="h-4 w-4" />
                        </a>
                      </>
                    )}
                    {isAdmin && (
                      <>
                        <button data-testid={`knowledge-edit-${item.id}`} onClick={() => openEdit(item)} className="text-[#4B5563] hover:text-[#1C1C1E]" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`knowledge-delete-${item.id}`} className="text-[#4B5563] hover:text-red-600" title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{item.title}"?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteResource(item.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <KnowledgeFormDialog open={formOpen} onOpenChange={setFormOpen} resource={editingResource} onCreated={load} />
    </DashboardLayout>
  );
}
